import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Coffee, Star, Sun, CloudSun, Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning, Sunset } from "lucide-react";

// True only on real desktops with a fine pointer — gates all mouse-tracking/tilt work.
export const useDesktopPointer = () => {
  const [d, setD] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(min-width: 1024px) and (pointer: fine)");
    const on = () => setD(m.matches);
    on();
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return d;
};

const WORDS = ["homestay", "tea estate retreat", "riverfront cottage", "bamboo villa", "jungle lodge"];

// Desktop: vertical sliding word carousel. Mobile/tablet: static "homestay" (zero animation).
export const HeroFlipWord = () => {
  const desktop = useDesktopPointer();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!desktop) return;
    const t = setInterval(() => setI((v) => (v + 1) % WORDS.length), 2500);
    return () => clearInterval(t);
  }, [desktop]);

  if (!desktop) return <span className="block text-brand font-extrabold">homestay</span>;

  return (
    <span className="block relative overflow-hidden" style={{ height: "1.2em" }} data-testid="hero-flip-word">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={WORDS[i]}
          initial={{ y: "115%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          exit={{ y: "-115%", opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="block whitespace-nowrap text-brand font-extrabold will-change-transform"
        >
          {WORDS[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

// Desktop-only floating social-proof pills that gently bob near the search capsule.
export const LiveActivityPills = () => {
  const desktop = useDesktopPointer();
  const [viewers, setViewers] = useState(4);
  useEffect(() => {
    if (!desktop) return;
    const t = setInterval(() => setViewers(3 + Math.floor(Math.random() * 5)), 8000);
    return () => clearInterval(t);
  }, [desktop]);
  if (!desktop) return null;

  const bob = (delay) => ({ animate: { y: [0, -6, 0] }, transition: { duration: 4, repeat: Infinity, ease: "easeInOut", delay } });

  return (
    <div className="hidden lg:flex items-center justify-between max-w-4xl mx-auto mt-5 px-2" data-testid="live-pills">
      <motion.div {...bob(0)} className="bg-white/85 backdrop-blur-md border border-black/10 shadow-md rounded-full px-3.5 py-1.5 flex items-center gap-2 text-xs font-medium text-ink-soft">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
        Live · {viewers} guests viewing stays in Assam right now
      </motion.div>
      <motion.div {...bob(0.8)} className="bg-white/85 backdrop-blur-md border border-black/10 shadow-md rounded-full px-3.5 py-1.5 flex items-center gap-2 text-xs font-medium text-ink-soft">
        <Coffee className="w-3.5 h-3.5 text-amber-600" />
        Top Pick · Heritage Tea Estate, Jorhat
        <span className="inline-flex items-center gap-0.5 text-ink font-semibold"><Star className="w-3 h-3 fill-[#F59E0B] text-[#F59E0B]" /> 4.98</span>
      </motion.div>
    </div>
  );
};

// WMO weather-code → icon + short "vibe" phrase.
const weatherVibe = (code) => {
  if (code === 0) return { Icon: Sun, vibe: "Clear skies · perfect for garden walks" };
  if (code <= 3) return { Icon: CloudSun, vibe: "Pleasant · great for exploring" };
  if (code === 45 || code === 48) return { Icon: CloudFog, vibe: "Misty valley mornings" };
  if (code >= 51 && code <= 67) return { Icon: CloudRain, vibe: "Light rain · cosy indoors" };
  if (code >= 71 && code <= 77) return { Icon: CloudSnow, vibe: "Chilly & crisp" };
  if (code >= 80 && code <= 82) return { Icon: CloudRain, vibe: "Passing showers" };
  if (code >= 95) return { Icon: CloudLightning, vibe: "Stormy skies" };
  return { Icon: Cloud, vibe: "Great weather for a getaway" };
};

const fmtTime = (iso) => {
  if (!iso || !iso.includes("T")) return null;
  let [h, m] = iso.split("T")[1].split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
};

const fetchWeather = async (lat, lng) => {
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,weather_code&daily=sunset&timezone=auto`;
  const r = await fetch(url);
  if (!r.ok) throw new Error("weather fetch failed");
  const d = await r.json();
  return { temp: Math.round(d?.current?.temperature_2m), code: d?.current?.weather_code ?? 1, sunset: d?.daily?.sunset?.[0] || null };
};

// Desktop-only ambient badges: LIVE temperature + weather vibe + real local sunset for the destination.
export const VibeBadges = ({ lat, lng }) => {
  const desktop = useDesktopPointer();
  const r1 = (n) => (n != null ? Math.round(n * 10) / 10 : null);
  const q = useQuery({
    queryKey: ["vibe-weather", r1(lat), r1(lng)],
    queryFn: () => fetchWeather(lat, lng),
    enabled: !!(desktop && lat != null && lng != null),
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  if (!desktop || lat == null || lng == null) return null;

  if (q.isLoading) {
    return <span className="hidden lg:inline-flex ml-3 h-6 w-48 rounded-full bg-paper-muted animate-pulse" data-testid="vibe-loading" aria-hidden="true" />;
  }
  const d = q.data;
  if (!d || Number.isNaN(d.temp)) return null;
  const { Icon, vibe } = weatherVibe(d.code);
  const sunny = Icon === Sun || Icon === CloudSun;
  const sunset = fmtTime(d.sunset);

  return (
    <>
      <span className="hidden lg:inline-flex items-center gap-1.5 bg-amber-50 text-amber-800 border border-amber-200 px-3 py-1 rounded-full text-xs font-medium ml-3" data-testid="vibe-weather">
        <motion.span
          className="inline-flex"
          animate={sunny ? { rotate: 360 } : { scale: [1, 1.15, 1], opacity: [1, 0.7, 1] }}
          transition={sunny ? { duration: 9, repeat: Infinity, ease: "linear" } : { duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <Icon className="w-3.5 h-3.5" />
        </motion.span>
        {d.temp}°C · {vibe}
      </span>
      {sunset && (
        <span className="hidden lg:inline-flex items-center gap-1.5 bg-sky-50 text-sky-800 border border-sky-200 px-3 py-1 rounded-full text-xs font-medium ml-2" data-testid="vibe-river">
          <Sunset className="w-3.5 h-3.5" /> Sunset at {sunset}
        </span>
      )}
    </>
  );
};
